/** @jsx jsx */

import Chart, {
  ChartWrapperOptions,
  GoogleChartWrapperChartType,
} from 'react-google-charts';
import { jsx, css, SerializedStyles } from '@emotion/core';
import { Colors, Typography } from '@zapier/design-system';
import _ from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { LoadingStatus } from 'app/developer-v3/pages/insights/tiles/Status';
import useWindowSize from 'app/developer-v3/hooks/useWindowSize';

const styleChartContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

// custom tooltip styles
// tooltip uses google-visualization-tooltip class
// https://developers.google.com/chart/interactive/docs/customizing_tooltip_content
export const styleTooltip = css`
  .google-visualization-tooltip {
    pointer-events: none;
    background-color: ${Colors.PrimeBlack};
    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    border: none;
    padding: 16px;

    & p {
      color: ${Colors.PrimeWhite};
      font-size: ${Typography.Body3};
      margin: 0px;
    }
  }
`;

interface CommonChartProps {
  chartType: GoogleChartWrapperChartType;
  chartData: readonly any[];
  containerStyle?: SerializedStyles;
  height?: string;
  colors?: string[];
  optionOverrides?: Partial<ChartWrapperOptions>;
  setHasRenderError: Dispatch<SetStateAction<boolean>>;
}

export function BaseChart({
  chartType,
  chartData,
  containerStyle,
  height,
  colors,
  optionOverrides,
  setHasRenderError,
}: CommonChartProps) {
  // The chart key is a hacky way of making the chart responsive to window size
  // changes. Without this work-around, the chart's width is responsive when the
  // window size increases, but the chart's width does *not* shrink when the
  // window size decreases.
  //
  // The strategy is to update the chart's `key` property when the window size
  // changes, which will force a full rerender of the component by destroying
  // the DOM element and creating a new one. Other lighter-handed approaches
  // like calling `chartWrapper.draw()` or using pure CSS didn't fix the issue.
  // This seems to be a somewhat common issue with the library:
  //
  // https://github.com/rakannimer/react-google-charts/issues/209
  const size = useWindowSize();
  const chartKey = `${size.width}x${size.height}`;

  return (
    <div css={[styleChartContainer, styleTooltip, containerStyle]}>
      <Chart
        chartEvents={[
          {
            // if there is an error rendering show the error state
            eventName: 'error',
            callback({ eventArgs }) {
              // `eventArgs` should include the error message according to
              // Google's documentation, but it doesn't actually seem to :(
              console.error('Error rendering chart!', ...eventArgs);
              setHasRenderError(true);
            },
          },
          {
            eventName: 'ready',
            callback({ chartWrapper }) {
              const chart = document.querySelector(
                `#${chartWrapper.getContainerId()}`
              );

              // get labels
              const labels = chart.getElementsByTagName('text');
              for (const label of Array.from(labels)) {
                // determine if x-axis label
                if (label.getAttribute('text-anchor') === 'middle') {
                  // increase distance from axis by 5
                  // Note: if the distance is too much the bottom of text will get clipped (ex: the 'g' in 'Aug')
                  // because it is outside of the chart viewport
                  const yCoord = parseFloat(label.getAttribute('y'));
                  label.setAttribute('y', String(yCoord + 5));
                }
              }
            },
          },
        ]}
        chartType={chartType}
        data={chartData}
        height={height || '100%'}
        key={chartKey}
        loader={<LoadingStatus />}
        options={_.merge(
          {
            colors,
            legend: { position: 'none' },
            // sets the chart area dimensions relative to the viewport
            // at 100% axis labels are outside the viewport
            chartArea: {
              top: 5,
              left: 34,
              right: 20,
              width: '100%',
              height: '85%',
            },
            backgroundColor: 'transparent',
            tooltip: {
              isHtml: true,
              trigger: 'visible',
            },
            fontSize: 12,
            fontName: Typography.Body1.font, // Inter,
            vAxis: {
              baselineColor: Colors.GrayWarm5,
              gridlines: {
                color: Colors.GrayWarm3,
              },
              textStyle: {
                color: Colors.PrimeBlack,
              },
            },
            hAxis: {
              gridlines: {
                color: 'transparent',
                count: 10,
              },
              textStyle: {
                color: Colors.PrimeBlack,
              },
              // Somehow this prevents Google Charts from rendering the same
              // date more than once on the x-axis
              ticks: chartData
                .slice(1)
                .map(dataPoint => new Date(dataPoint[0])),
            },
          },
          optionOverrides
        )}
        width="100%"
      />
    </div>
  );
}
